"use client";

import * as React from "react";
import { signIn } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { z } from "zod";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";

export function LoginForm() {
  const locale = useLocale();
  const t = useTranslations("auth");

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  // Client-seitige Validierung (die verbindliche Prüfung macht der Server).
  const loginSchema = React.useMemo(
    () =>
      z.object({
        email: z.string().email({ message: t("invalidEmail") }),
        password: z.string().min(1, { message: t("passwordRequired") }),
      }),
    [t],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("loginError"));
      return;
    }

    setIsLoading(true);
    try {
      const result = await signIn("credentials", {
        email: parsed.data.email,
        password: parsed.data.password,
        redirect: false,
      });

      if (!result || result.error) {
        setError(t("invalidCredentials"));
        return;
      }

      // Voller Reload, damit die frisch gesetzte Session serverseitig sicher
      // greift (Locale-Präfix bleibt erhalten, Middleware regelt den Rest).
      window.location.href = `/${locale}/dashboard`;
    } catch {
      setError(t("loginError"));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{
        backgroundImage:
          "linear-gradient(135deg, color-mix(in oklch, var(--brand) 14%, var(--background)) 0%, color-mix(in oklch, var(--brand-2) 12%, var(--background)) 100%)",
      }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl text-white shadow-[0_6px_20px_-4px_var(--brand)]"
            style={{
              backgroundImage:
                "linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%)",
            }}
          >
            <Building2 className="h-7 w-7" />
          </div>
          <CardTitle className="text-2xl">{t("loginTitle")}</CardTitle>
          <CardDescription>{t("loginSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@lts-immo.de"
                required
                autoComplete="email"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t("loggingIn") : t("loginButton")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
